import { createFileRoute, Link, Navigate, redirect, useRouterState } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { useAbility } from '@features/authorization';
import {
  getUserProfilesDirectoryReturnSearch,
  userProfilesDirectorySortSchema,
} from '@features/crm-customer-directory';
import type { RouterContext } from '@shared/config/router-context';
import {
  canAccessCrmCustomerDocuments,
  canAccessCrmUserProfilePayments,
  canAccessHelpdesk,
} from '@shared/lib';
import { Alert, AlertDescription, AlertTitle, Button } from '@shared/ui';
import {
  CrmCustomerRecordFullPageAccessGate,
  CrmCustomerRecordShell,
  findCrmCustomerRecordArea,
  firstAccessibleCrmCustomerRecordArea,
} from '@widgets/crm-customer-record-shell';

import { crmCustomerRecordSearchSchema } from '../../-user-profile-search';

const nestedProfilePath =
  /^(documents\/(?:new|[0-9a-f-]{36})|training\/[0-9a-f-]{36}|learning\/(?:courses|registrations|certifications|education-module-logs)\/[0-9a-f-]{36})$/i;

/** Retained browser aliases use the same target authorization before replacing their URL. */
export const Route = createFileRoute('/$locale/_authenticated/crm/customers/$customerId')({
  validateSearch: crmCustomerRecordSearchSchema,
  beforeLoad: ({ context, location, params }) => {
    const { auth, ability } = context as RouterContext;
    if (auth.isLoading || !auth.isAuthenticated) return;
    const area = location.pathname.split(`/customers/${params.customerId}/`)[1]?.split('/')[0];
    if (
      (area === 'documents' && !canAccessCrmCustomerDocuments(ability)) ||
      (area === 'payments' && !canAccessCrmUserProfilePayments(ability)) ||
      (area === 'helpdesk' && !canAccessHelpdesk(ability))
    ) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({
        to: '/$locale/unauthorized',
        params: { locale: params.locale },
        replace: true,
      });
    }
  },
  component: LegacyCrmUserProfileRoute,
});

function LegacyCrmUserProfileRoute() {
  const ability = useAbility();
  const { customerId, locale } = Route.useParams();
  const search = Route.useSearch();
  const { t } = useTranslation();
  const location = useRouterState({ select: (state) => state.location });
  const legacyRoot = `/${locale}/crm/customers/${customerId}`;
  // The old match may remain mounted while replace navigation loads its destination.
  if (location.pathname !== legacyRoot && !location.pathname.startsWith(`${legacyRoot}/`)) {
    return null;
  }
  const suffix = location.pathname.slice(legacyRoot.length).replace(/^\/+|\/+$/g, '');
  const nestedId = suffix.split('/').at(-1);
  const validNestedId = suffix === 'documents/new' || z.uuid().safeParse(nestedId).success;
  const fullPage = nestedProfilePath.test(suffix) && validNestedId;
  const areaKey = fullPage ? suffix.split('/')[0] : suffix || 'overview';
  const area = findCrmCustomerRecordArea(areaKey);
  const activeTabId =
    areaKey === 'payments' ? 'payments' : areaKey === 'helpdesk' ? 'communications' : area?.id;
  const backAction = (
    <Button asChild variant="ghost" size="sm">
      <Link
        to="/$locale/crm"
        params={{ locale }}
        search={getUserProfilesDirectoryReturnSearch({
          ...search,
          sort: userProfilesDirectorySortSchema.safeParse(search.sort).data,
        })}
      >
        {t('crm.customerProfile.backToDirectory', 'Customer directory')}
      </Link>
    </Button>
  );

  // Classify malformed identifiers and unknown deep paths before requesting target context.
  if (!z.uuid().safeParse(customerId).success || activeTabId === undefined) {
    return (
      <main className="min-w-0 p-4 sm:p-6">
        {backAction}
        <Alert>
          <AlertTitle>{t('crm.customerRecord.invalidAreaTitle', 'Page not found')}</AlertTitle>
          <AlertDescription>
            {t(
              'crm.customerRecord.invalidAreaDescription',
              'This customer-record area does not exist.'
            )}
          </AlertDescription>
        </Alert>
      </main>
    );
  }

  if (
    (areaKey === 'documents' && !canAccessCrmCustomerDocuments(ability)) ||
    (areaKey === 'payments' && !canAccessCrmUserProfilePayments(ability)) ||
    (areaKey === 'helpdesk' && !canAccessHelpdesk(ability))
  ) {
    return <Navigate to="/$locale/unauthorized" params={{ locale }} replace />;
  }

  const canonicalHref = `/${locale}/crm/user-profiles/${customerId}${suffix ? `/${suffix}` : ''}${location.searchStr}${location.hash ? `#${location.hash}` : ''}`;
  const destination = (
    <Navigate from={Route.fullPath} href={canonicalHref} replace resetScroll={false} />
  );
  const fallback = (route: '/$locale/unauthorized' | NonNullable<typeof area>['route']) => (
    <Navigate
      to={route ?? '/$locale/unauthorized'}
      params={{ userProfileId: customerId, locale }}
      search={search}
      hash={location.hash}
      replace
      resetScroll={false}
    />
  );

  if (fullPage && (areaKey === 'documents' || areaKey === 'learning' || areaKey === 'training')) {
    return (
      <CrmCustomerRecordFullPageAccessGate
        customerId={customerId}
        requiredArea={areaKey}
        onAccessUnavailable={(allowedAreas) =>
          fallback(
            firstAccessibleCrmCustomerRecordArea(allowedAreas)?.route ?? '/$locale/unauthorized'
          )
        }
      >
        {() => destination}
      </CrmCustomerRecordFullPageAccessGate>
    );
  }

  return (
    <main className="min-w-0 p-4 sm:p-6">
      <CrmCustomerRecordShell
        activeTabId={activeTabId}
        customerId={customerId}
        backAction={backAction}
        renderAreaLink={(tab, children, { className, isActive }) => (
          <Link
            key={tab.id}
            to={tab.route}
            params={{ userProfileId: customerId, locale }}
            search={search}
            className={className}
            aria-current={isActive ? 'page' : undefined}
          >
            {children}
          </Link>
        )}
        renderAreaRedirect={(tab) => fallback(tab.route)}
      >
        {destination}
      </CrmCustomerRecordShell>
    </main>
  );
}
